import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth, roleHomePath } from "@/contexts/AuthContext";
import { Car } from "lucide-react";
import { toast } from "sonner";

const DEMO_LOGINS = [
  { role: "Admin", email: "admin@carfin.com", password: "demo123" },
  { role: "Underwriter", email: "underwriter@carfin.com", password: "demo123" },
  { role: "Operations", email: "operations@carfin.com", password: "demo123" },
  { role: "Collections", email: "collections@carfin.com", password: "demo123" },
  { role: "Dealer", email: "dealer@carfin.com", password: "demo123" },
  { role: "DSA", email: "dsa@carfin.com", password: "demo123" },
  { role: "Borrower", email: "borrower@carfin.com", password: "demo123" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.name}`);
      navigate(roleHomePath(res.user.role));
    } else {
      setError(res.error);
    }
  };

  const quickLogin = async (creds) => {
    setEmail(creds.email);
    setPassword(creds.password);
    setSubmitting(true);
    const res = await login(creds.email, creds.password);
    setSubmitting(false);
    if (res.ok) navigate(roleHomePath(res.user.role));
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between gradient-navy text-white p-12">
        <Link to="/" className="flex items-center gap-3" data-testid="login-brand">
          <img src="/assets/carfin-logo.png" alt="CarFin India" className="h-10" />
          <div className="leading-tight border-l border-slate-700 pl-3">
            <div className="text-[10px] uppercase tracking-wider text-teal-300">Red Fort Capital NBFC</div>
          </div>
        </Link>
        <div>
          <h2 className="text-4xl font-semibold leading-tight">Sign in to your<br/>CarFin workspace.</h2>
          <p className="text-slate-300 mt-4 max-w-md">Borrowers, dealers, DSAs, underwriters and operations teams operate every used car loan from one transparent workspace.</p>
        </div>
        <div className="text-xs text-slate-400">© {new Date().getFullYear()} Red Fort Capital Finance NBFC</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-6">
            <img src="/assets/carfin-logo.png" alt="CarFin India" className="h-9" />
          </Link>
          <h1 className="text-2xl font-semibold text-slate-900">Sign in</h1>
          <p className="text-sm text-slate-600 mt-1">Use your CarFin credentials.</p>
          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.in" required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
            <Button type="submit" data-testid="login-submit-btn" className="w-full bg-slate-900 hover:bg-slate-800" disabled={submitting}>{submitting ? "Signing in…" : "Sign in"}</Button>
          </form>

          <Card className="p-4 mt-6 border-slate-200 bg-white">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Demo accounts</div>
              <div className="text-[10px] uppercase tracking-wider text-teal-700 font-semibold font-mono">all use: demo123</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_LOGINS.map(d => (
                <button
                  key={d.role}
                  type="button"
                  data-testid={`quick-login-${d.role.toLowerCase()}`}
                  onClick={() => quickLogin(d)}
                  className="text-left px-3 py-2 border border-slate-200 rounded-md hover:border-teal-500 hover:bg-teal-50 transition-colors text-sm"
                >
                  <div className="font-medium text-slate-900">{d.role}</div>
                  <div className="text-xs text-slate-500 truncate">{d.email}</div>
                </button>
              ))}
            </div>
          </Card>

          <div className="mt-6 text-sm text-slate-600 text-center">
            New here? <Link to="/apply" className="text-teal-700 font-medium">Apply for a loan</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
